/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react";
import { IonApp, setupIonicReact, useIonRouter } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { Capacitor } from "@capacitor/core";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import AppRoutes from "./AppRoutes";
import { AuthProvider } from "./contexts/AuthContext";
import useAuth from "./hooks/useAuth";
import ConsentGate from "./components/auth/ConsentGate";
import StaffPortalPrompt from "./components/auth/StaffPortalPrompt";
import { notifyInvitationTokenChanged } from "./services/staffPortal";
import { BLEProvider } from "./contexts/BleContext";
import { useHistory } from "react-router-dom";
import { StorageProvider } from "./contexts/StorageContext";
import { Preferences } from "@capacitor/preferences";
import { Browser } from "@capacitor/browser";
import { SnackbarProvider } from "./components/ui/snackbar/Snackbar";
import { ToasterProvider } from "./components/ui/toastbar/Toaster";
import { PushNotifications } from "@capacitor/push-notifications";
import { FCM } from "@capacitor-community/fcm";
import { crashlytics } from "./services/crashlytics";
import { initializeErrorHandling } from "./services/errorHandler";
import ErrorBoundary from "./components/ErrorBoundary";
import { ScreenTracker } from "./components/ScreenTracker";
import analytics from "./services/firebaseAnalytics";
import AppVersionManager from "./components/AppVersionManager";

setupIonicReact({
  rippleEffect: true,
  swipeBackEnabled: true,
  mode: "md",
});

// ✅ FIXED: Move FCM setup to a proper hook
const useFCMSetup = () => {
  const setupCompleteRef = useRef(false);

  useEffect(() => {
    crashlytics.initialize();
    analytics.initialize();
    initializeErrorHandling();
    if (setupCompleteRef.current) return;
    setupCompleteRef.current = true;

    console.log("🔧 Setting up FCM token handler");

    // Setup FCM token handler
    (window as any).setFCMTokenFromNative = async (token: string) => {
      console.log("Setting FCM token from native:", token);
      localStorage.setItem("fcmToken", token);
      await Preferences.set({
        key: "fcmToken",
        value: token,
      });
      (window as any).fcmToken = token;
      // Trigger custom event
      window.dispatchEvent(new CustomEvent("fcm-token", { detail: token }));
    };

    return () => {
      // Cleanup on unmount
      delete (window as any).setFCMTokenFromNative;
    };
  }, []);
};

// ✅ FIXED: App URL listener with proper cleanup
const AppUrlListener: React.FC = () => {
  const history = useHistory();
  const { completeSocialLogin } = useAuth();
  const listenerRef = useRef<any>(null);
  // Capture search string synchronously at init — before React Router's <Redirect>
  // fires during render and overwrites window.location
  const initialSearch = useRef(window.location.search);

  // Handle web: extract invitation token from URL query string on initial load
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    const params = new URLSearchParams(initialSearch.current);
    const invitation_token = params.get("param1");
    if (invitation_token) {
      Preferences.set({ key: "invitation_token", value: invitation_token }).then(() => {
        // R-753: a pending share invite holds back the staff portal pop-up.
        notifyInvitationTokenChanged();
        history.replace("/signin?register=false");
      });
    }
  }, []);

  useEffect(() => {
    console.log("🔗 Setting up App URL listener");

    const handleAppUrlOpen = async (data: any) => {
      console.log("🔗 App URL Opened:", data);
      if (data?.url) {
        try {
          const url = new URL(data.url);

          // ⚠️ The Cognito social callback MUST be intercepted here, before the generic
          // push below. `new URL("arcconnect://auth/callback?code=…")` parses the custom
          // scheme as host="auth", pathname="/callback" — so the generic handler would
          // push "/callback", a route that does not exist, and the sign-in would die
          // silently with the code still unredeemed. Verified, not assumed.
          if (url.protocol === "arcconnect:" && url.host === "auth") {
            const params = new URLSearchParams(url.search);
            const code = params.get("code");
            if (code) {
              // Close the Hosted-UI tab first: on Android it stays on top of the app and
              // the user watches a browser instead of the dashboard.
              await Browser.close().catch(() => undefined);
              await completeSocialLogin(code, params.get("state"));
              return;
            }
            // Cognito reports provider-side failures on the same callback.
            console.error(
              "🔗 Social callback carried no code:",
              params.get("error_description") || params.get("error") || "(no detail)",
            );
            await Browser.close().catch(() => undefined);
            history.replace("/signin?register=false");
            return;
          }

          const path = url.pathname + url.search + url.hash;
          // console.log("🔗 Universal Link triggered:", path);
          const params = new URLSearchParams(url.search);
          const invitation_token = params.get("param1");
          if (invitation_token) {
            await Preferences.set({
              key: "invitation_token",
              value: invitation_token,
            });
            notifyInvitationTokenChanged();
          }
          history.push(path);
        } catch (error) {
          console.error("🔗 Error parsing URL:", error);
        }
      }
    };

    // Setup listener and store reference
    const setupListener = async () => {
      try {
        const listener = await CapacitorApp.addListener(
          "appUrlOpen",
          handleAppUrlOpen
        );
        listenerRef.current = listener;
        console.log("🔗 App URL listener registered successfully");
      } catch (error) {
        console.error("🔗 Failed to register App URL listener:", error);
      }
    };

    setupListener();

    // Cleanup function
    return () => {
      console.log("🧹 Cleaning up App URL listener");
      if (listenerRef.current) {
        try {
          // Method 1: Try the remove method
          if (typeof listenerRef.current.remove === "function") {
            listenerRef.current.remove();
            console.log("🧹 App URL listener removed successfully");
          }
          // Method 2: If it's a function itself (unsubscribe pattern)
          else if (typeof listenerRef.current === "function") {
            listenerRef.current();
            console.log("🧹 App URL listener unsubscribed successfully");
          }
          // Method 3: Fallback to removing all listeners
          else {
            console.warn("🧹 Using fallback cleanup method");
            CapacitorApp.removeAllListeners();
          }
        } catch (error) {
          console.error("🧹 Error removing App URL listener:", error);
          // Last resort: remove all listeners for this event
          try {
            CapacitorApp.removeAllListeners();
          } catch (fallbackError) {
            console.error("🧹 Fallback cleanup also failed:", fallbackError);
          }
        }
      }
    };
  }, [history]);

  return null;
};

// ✅ FIXED: Push notifications handler
const PushNotificationHandler: React.FC = () => {
  const initializeRef = useRef(false);
  const history = useHistory();
  const router = useIonRouter();

  useEffect(() => {
    if (initializeRef.current) return;
    initializeRef.current = true;

    console.log("🔔 Setting up push notifications");

    const initializePushNotifications = async () => {
      try {
        // ✅ FIXED: Call the hook function directly, not inside useEffect
        console.log("📱 initializing push notifications");
        const setup = async () => {
          // Request permissions and register for push
          const permStatus = await PushNotifications.requestPermissions();
          console.log(
            "Push notification permission status:",
            JSON.stringify(permStatus)
          );
          if (permStatus.receive === "granted") {
            await PushNotifications.register();
          }

          // Get FCM token (recommended for server use)
          try {
            const { token } = await FCM.getToken();
            console.log("🔑 FCM Token:", token);
            // Store token in Capacitor Preferences
            await Preferences.set({ key: "fcmToken", value: token });
          } catch (err) {
            console.error("❌ Error getting FCM token", err);
          }

          // Listen for push notifications
          PushNotifications.addListener(
            "pushNotificationReceived",
            (notification) => {
              console.log("📩 Push received:", notification);
            }
          );

          PushNotifications.addListener(
            "pushNotificationActionPerformed",
            (action) => {
              const pathFound = action.notification.data?.path;
              // Navigate based on notification data if needed
              pathFound && router && router.push("/" + pathFound || "/home");
              console.log(
                "👆 Notification tapped: App",
                action.notification,
                pathFound
              );
            }
          );

          PushNotifications.addListener("registration", (token) => {
            // This is the APNs token on iOS, not FCM token!
            console.log("🍏 APNs Token (iOS):", token.value);
          });

          PushNotifications.addListener("registrationError", (err) => {
            console.error("⚠️ Registration error:", err.error);
          });
        };
        // Call your push notification setup function
        // Note: If usePushNotifications is a hook, we need to refactor it
        // For now, let's assume it's a function that can be called directly

        setup();
        // await usePushNotifications();
      } catch (error) {
        console.error("🔔 Error setting up push notifications:", error);
      }
    };

    if (Capacitor.isNativePlatform()) {
      initializePushNotifications();
    }
  }, []);

  return null;
};

// ✅ FIXED: FCM Setup Component
const FCMSetup: React.FC = () => {
  useFCMSetup();
  return null;
};

// ✅ FIXED: Main App component with proper structure
const App: React.FC = () => {
  console.log("🚀 App component rendering");
  useEffect(() => {
    const showAndHideSplash = async () => {
      try {
        await SplashScreen.show({
          showDuration: 2000, // Optional: show for 2 seconds
          autoHide: true, // We'll hide it manually
        });
      } catch (e) {
        // Ignore if not supported on web
      }
      setTimeout(async () => {
        try {
          await SplashScreen.hide();
        } catch (e) {
          // Ignore if not supported on web
        }
      }, 2000);
    };
    showAndHideSplash();
  }, []);

  return (
    <IonApp>
      <IonReactRouter>
        {/* ✅ FCM setup inside React context */}
        <FCMSetup />

        {/* ✅ All components using hooks are INSIDE Router context */}
        <PushNotificationHandler />
        <ScreenTracker />

        {/* Global Version Checker */}
        <AppVersionManager />

        {/* ✅ Context providers in correct order */}
        <ErrorBoundary>
          <SnackbarProvider>
            <ToasterProvider>
              <StorageProvider>
                <AuthProvider>
                  {/* ⚠️ Moved INSIDE AuthProvider: it completes the Cognito social
                      callback, which needs completeSocialLogin from the auth context.
                      Still inside IonReactRouter, so useHistory keeps working. */}
                  <AppUrlListener />
                  <BLEProvider>
                    <AppRoutes />
                    {/* Mounted INSIDE AuthProvider (it reads consentsOutstanding) and
                        alongside the routes rather than wrapping them, so it overlays
                        whatever screen the user is on. It renders null unless the kernel
                        says something is outstanding. See ConsentGate for why the app
                        needs this even though the kernel does the actual enforcing. */}
                    <ConsentGate />
                    {/* Provider staff only (R-753): the one-time "Go to Provider Portal /
                        Stay in Patient App" choice. Renders null for everyone else. */}
                    <StaffPortalPrompt />
                  </BLEProvider>
                </AuthProvider>
              </StorageProvider>
            </ToasterProvider>
          </SnackbarProvider>
        </ErrorBoundary>
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
